// ============================================================================
// Harvest bins — Phase 2 of docs/harvest-and-labour-plan.md, /api/harvest
// (HHNodeServer routes/harvest.js).
// ============================================================================

export type BinStatus = 'started' | 'full' | 'tagged' | 'loaded' | 'delivered' | 'received' | 'rejected' | 'void';
export type BinLocationSource = 'start_fix' | 'presence_ping' | 'job_target' | 'manual';
export type LabelSize = '100x150' | '100x75' | '62x100' | '54x101';

export interface BinPicker {
  worker_id: number;
  first_name: string;
  last_name: string;
  preferred_name: string | null;
  picker_code: string;
  share: number;
}

export interface Bin {
  id: number;
  season_year: number;
  local_date: string;
  field_code: string | null;
  tag_code: string | null;
  unit_type_id: number | null;
  unit_type_name: string | null;
  job_id: number | null;
  job_name: string | null;
  farm_id: number | null;
  farm_name: string | null;
  block_id: number | null;
  block_name: string | null;
  cultivar_area_id: number | null;
  cultivar_area_name: string | null;
  cultivar_name: string | null;
  row_number: number | null;
  location_source: BinLocationSource;
  needs_review: boolean;
  review_reason: string | null;
  status: BinStatus;
  started_at: string;
  finished_at: string | null;
  fill_fraction: number;
  est_net_kg: number | null;
  received_at: string | null;
  voided_at: string | null;
  void_reason: string | null;
  notes: string | null;
  pickers: BinPicker[];
  /** Shed lookup only: another untagged bin that day carries the same chalk code. */
  duplicate_field_code?: boolean;
}

export interface BinUnitType {
  id: number;
  organisation_id: number | null;
  type_key: string;
  name: string;
  nominal_net_kg: number | null;
  is_builtin: boolean;
  active: boolean;
}

export interface HarvestSettings {
  label_size: LabelSize;
  season_start_month: number;
  default_unit_type_id: number | null;
  /** Write actual harvest windows from bins (default on). */
  record_actual_windows?: boolean;
  /** Let first/last pick dates feed cultivar maturity by region, seen by other orgs (default off). */
  share_harvest_dates?: boolean;
  label_sizes: LabelSize[];
}

export interface PickingJob {
  id: number;
  name: string;
  status: string;
  target_name: string | null;
  farm_id: number | null;
  farm_name: string | null;
  activity_type_name: string | null;
}

export interface StartBinsInput {
  client_uuid: string;
  job_id: number;
  count?: number;
  field_codes?: string[];
  worker_ids?: number[];
  unit_type_id?: number;
  lat?: number | null;
  lng?: number | null;
  accuracy?: number | null;
  row_number?: number | null;
  at?: string;
}

export const LABEL_SIZE_LABELS: Record<LabelSize, string> = {
  '100x150': '100 × 150 mm (4 × 6")',
  '100x75': '100 × 75 mm (4 × 3")',
  '62x100': '62 × 100 mm (Brother QL)',
  '54x101': '54 × 101 mm (Dymo)',
};

export function binPlace(b: Pick<Bin, 'block_name' | 'cultivar_area_name' | 'farm_name'>): string {
  return [b.block_name, b.cultivar_area_name].filter(Boolean).join(' › ') || b.farm_name || 'Place to confirm';
}

// ---- Phase 3: loads and packouts -------------------------------------------

export type LoadStatus = 'building' | 'dispatched' | 'delivered' | 'void';

export interface BinWhp {
  status: 'clear' | 'within_whp' | 'unknown';
  sprays: { spray_id: number; title: string; applied: string; whp_clear_date: string }[];
}

export interface HarvestLoad {
  id: number;
  season_year: number;
  load_number: number;
  destination_name: string | null;
  grower_number: string | null;
  vehicle_equipment_id: number | null;
  vehicle_name: string | null;
  vehicle_text: string | null;
  driver_worker_id: number | null;
  driver_worker_name: string | null;
  driver_name: string | null;
  carrier_name: string | null;
  status: LoadStatus;
  dispatched_at: string | null;
  delivered_at: string | null;
  notes: string | null;
  bin_count: number;
  est_kg: number;
  created_at: string;
  bins?: (Bin & { whp: BinWhp })[];
}

export interface LoadInput {
  destination_name?: string | null;
  grower_number?: string | null;
  vehicle_text?: string | null;
  driver_name?: string | null;
  carrier_name?: string | null;
  notes?: string | null;
}

export type AddBinStatus = 'added' | 'already' | 'other_load' | 'not_tagged' | 'void' | 'not_found' | 'invalid';

export interface AddBinResult {
  input: string;
  status: AddBinStatus;
  message?: string;
  tag_code?: string;
  bin_id?: number;
}

export type GradeKind = 'saleable' | 'processing' | 'waste';

export interface PackoutGrade {
  id: number;
  organisation_id: number | null;
  name: string;
  kind: GradeKind;
  sort_order: number;
  is_builtin: boolean;
  active: boolean;
}

export interface PackoutGradeLine {
  grade_id: number;
  name: string;
  kind: GradeKind;
  kg: number;
}

export interface Packout {
  id: number;
  load_id: number | null;
  load_number: number | null;
  load_season: number | null;
  packout_date: string;
  packhouse_name: string | null;
  reference: string | null;
  total_kg_tipped: number;
  notes: string | null;
  grades: PackoutGradeLine[];
  bin_count: number;
  bins?: (Bin & { allocated_kg: number })[];
}

export interface PackoutInput {
  packout_date: string;
  packhouse_name?: string | null;
  reference?: string | null;
  total_kg_tipped: number;
  load_id?: number | null;
  grades: { grade_id: number; kg: number }[];
  bin_ids: number[];
  notes?: string | null;
}

export type PackoutGroupKey = 'cultivar_area' | 'block' | 'cultivar' | 'farm' | 'pick_date' | 'packout';

export interface PackoutReportRow {
  [key: string]: unknown;
  bins: number;
  kg: number;
  tonnes: number;
  tonnes_per_ha: number | null;
  area_hectares: number | null;
  grades: { grade_id: number; name: string; kind: GradeKind; kg: number; pct: number | null }[];
}

export interface PackoutReport {
  group_by: PackoutGroupKey;
  rows: PackoutReportRow[];
  totals: { bins: number; kg: number; tonnes: number };
  grades: PackoutGrade[];
}

export const PACKOUT_GROUPS: Record<PackoutGroupKey, { name: string; label: string }> = {
  cultivar_area: { name: 'cultivar_area_name', label: 'Cultivar area' },
  block: { name: 'block_name', label: 'Block' },
  cultivar: { name: 'cultivar_name', label: 'Cultivar' },
  farm: { name: 'farm_name', label: 'Farm' },
  pick_date: { name: 'local_date', label: 'Pick date' },
  packout: { name: 'packout_label', label: 'Packout' },
};

export function loadLabel(l: Pick<HarvestLoad, 'season_year' | 'load_number'>): string {
  return `${String(l.season_year % 100).padStart(2, '0')}-${String(l.load_number).padStart(3, '0')}`;
}

// ============================================================================
// Phase 5: harvest feedback
// ============================================================================

export type WindowAction = 'create' | 'update' | 'same' | 'differs';

export interface ActualWindowRow {
  cultivar_area_id: number;
  cultivar_area_name: string;
  block_name: string | null;
  farm_name: string;
  cultivar_name: string | null;
  bin_season: number;
  window_year: number;
  first_pick: string;
  last_pick: string;
  bins: number;
  existing: { id: number; season_year: number; status: 'estimate' | 'confirmed' | 'actual'; source: 'manual' | 'bins';
    earliest_harvest_date: string; expected_end_date: string | null; notes: string | null } | null;
  action: WindowAction;
  proposed: { earliest_harvest_date: string; expected_end_date: string };
  ongoing: boolean;
  applied: boolean;
}

export interface ActualWindows {
  record_actual_windows: boolean;
  today: string;
  rows: ActualWindowRow[];
}

export interface YieldSeason {
  season_year: number;
  first_pick: string;
  last_pick: string;
  tree_age: number | null;
  bins: number;
  tonnes: number;
  weighed_tonnes: number;
  bins_per_ha: number | null;
  tonnes_per_ha: number | null;
}

export type SuggestionBasis = 'young_trend' | 'average' | 'last_season';

export interface YieldArea {
  cultivar_area_id: number;
  cultivar_area_name: string;
  block_name: string | null;
  farm_name: string;
  farm_id: number;
  cultivar_id: number | null;
  cultivar_name: string | null;
  area_hectares: number | null;
  tree_count: number | null;
  seasons: YieldSeason[];
  suggested_bins: number | null;
  basis: SuggestionBasis | null;
}

export interface YieldHistory {
  seasons: number[];
  areas: YieldArea[];
  by_cultivar_age: { cultivar_id: number; cultivar_name: string | null; tree_age: number; seasons: number; tonnes_per_ha: number }[];
}

export const SUGGESTION_BASIS: Record<SuggestionBasis, string> = {
  young_trend: 'young trees: last season plus their recent rise',
  average: 'average of the last three seasons per hectare',
  last_season: 'last season per hectare',
};
